import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, merge } from 'rxjs';

import { LoggedUserService } from '@core/services/admin/logged-user.service';
import { DeviceDetectorService } from '@core/services/device-detector.service';
import {
    ConversationAssignmentUpdateParams,
    MessagingAssignmentsHttpService,
} from '@features/inbox/services/messaging-assignments.http.service';
import { MOBILE_SLUGS } from '@routes/mobile.routes';
import { APP_SLUGS, MAIN_SLUGS } from '@routes/routes.types';
import { ToastService } from '@shared/services/toast.service';

import { VoiceHttpService } from '../voice-http.service';
import {
    isOutboundVoiceEvent,
    isWhatsappVoiceCall,
    VOICE_CALL_DISPOSITION,
    VOICE_CALL_PROVIDER,
    VOICE_CALL_STATUS,
    VOICE_CONVERSATION_STATE,
    VoiceCall,
    VoiceCallContext,
    VoiceCallDisposition,
    VoiceConversationEvent,
    VoiceOutboundOrigin,
    VoiceOutboundResult,
} from '../voice.types';
import { VoiceConversationStore } from './voice-conversation.store';
import { VoiceMicDeniedError, VoiceWhatsappClientService } from './voice-whatsapp-client.service';
import { VoiceWebrtcClientService } from './voice-webrtc-client.service';

/**
 * Orchestrates picking up a transferred voice call and holds the caller context for
 * the call panel. Two mutually exclusive legs, chosen per queue entry by `provider`:
 *
 * - Telnyx / Twilio / SIP: the Telnyx WebRTC SDK is registered up-front, the backend
 *   bridges the call to its sip_address and the SDK auto-answers.
 * - WhatsApp Cloud: the browser negotiates its own RTCPeerConnection with the backend
 *   (SDP offer on pickup, answer back), no SDK involved.
 *
 * Either way the conversation is assigned to the agent who picked up and the inbox
 * lands on it.
 *
 * KAN-238 — outbound: `placeCall` originates through the backend, then waits for the
 * WAITING_AGENT frame flagged `direction: "outbound"` (the callee answered) and picks
 * the call up automatically with the `pickup_token` only this browser received. The
 * same two legs apply, chosen by the provider the backend reports for the call.
 */
@Injectable({ providedIn: 'root' })
export class VoiceCallService {
    private readonly voiceS = inject(VoiceHttpService);
    private readonly voiceStore = inject(VoiceConversationStore);
    private readonly webrtc = inject(VoiceWebrtcClientService);
    private readonly whatsapp = inject(VoiceWhatsappClientService);
    private readonly toast = inject(ToastService);
    private readonly loggedUserS = inject(LoggedUserService);
    private readonly assignmentsHttpS = inject(MessagingAssignmentsHttpService);
    private readonly router = inject(Router);
    private readonly detectorS = inject(DeviceDetectorService);

    /** The leg currently in use ('whatsapp' while a WhatsApp call is negotiating or live). */
    private readonly activeLeg = computed<'whatsapp' | 'telnyx'>(() =>
        this.whatsapp.status() !== 'idle' ? 'whatsapp' : 'telnyx'
    );

    /** Unified call state for the call panel: 'active' once audio is flowing on either leg. */
    readonly status = computed(() =>
        this.activeLeg() === 'whatsapp' ? this.whatsapp.status() : this.webrtc.status()
    );
    readonly muted = computed(() =>
        this.activeLeg() === 'whatsapp' ? this.whatsapp.muted() : this.webrtc.muted()
    );
    /** Telnyx WebRTC is configured on the backend (not 503). WhatsApp doesn't depend on it. */
    readonly available = this.webrtc.available;
    /** True once the Telnyx SDK is registered — gates Telnyx pickups, not WhatsApp ones. */
    readonly registered = this.webrtc.registered;
    /** The agent is on a live call — every other "pick up" must be gated while true. */
    readonly onCall = computed(
        () => this.webrtc.status() === 'active' || this.whatsapp.status() === 'active'
    );
    /**
     * Conversation of OUR live call (null when not on one). This is the strongest truth
     * for call-state UI: while a leg is active, no stale/late STOMP frame may present
     * that conversation as "waiting for an agent".
     */
    readonly activeCallConversationId = computed(() =>
        this.onCall() ? (this.context()?.conversationId ?? null) : null
    );
    /** Fires when the active call ends — the queue widget uses it to offer the next one. */
    readonly callEnded$ = merge(this.webrtc.callEnded$, this.whatsapp.callEnded$);

    /** Caller shown in the call panel while a call is being taken; null = no panel. */
    readonly context = signal<VoiceCallContext | null>(null);

    /** conversation_id currently being picked up (spinner on that row). */
    readonly pickingUpId = signal<number | null>(null);
    /** An outbound call is being originated (spinner on the "Llamar" CTA). */
    readonly placingCall = signal(false);
    /**
     * KAN-238: an outbound call left and is ringing the callee — no leg is up yet, the
     * call bar reads "Llamando…" and "hang up" cancels the attempt.
     */
    readonly outboundRinging = signal(false);

    /**
     * The outbound call this browser placed and hasn't answered yet: its originate 200
     * (provider_call_id + pickup_token) and the bar context. Cleared on pickup, cancel,
     * no-answer or when the backend reports the call ended.
     */
    private readonly pendingOutbound = signal<{
        call: VoiceCall;
        token: string;
        context: VoiceCallContext;
    } | null>(null);
    /** Times the ring of an outbound call out (callee never answered). */
    private ringWatchdog?: ReturnType<typeof setTimeout>;

    /** Guards against a bridge that never rings back after a 200 pickup. */
    private bridgeWatchdog?: ReturnType<typeof setTimeout>;

    /**
     * Conversation of the call WE picked up. A picked-up call gets no ENDED frame over
     * STOMP (it continues on another leg), so when our leg hangs up we flip the store
     * entry to ENDED ourselves — otherwise the composer stays on "call in progress".
     */
    private pickedConversationId: number | null = null;

    /**
     * Last Telnyx pickup attempted, kept until its call ends. The backend has been seen
     * bridging the call even when /pickup responds with an error — the catch clears
     * `context`, leaving the agent mid-call with no bar (no mute/hang-up). If the SDK
     * goes active with no context, this is what we restore the UI from.
     */
    private lastAttempt: { event: VoiceConversationEvent; context: VoiceCallContext } | null = null;

    constructor() {
        // Drop the panel once the call hangs up (locally or remotely), on either leg.
        this.callEnded$.subscribe(() => {
            if (this.pickedConversationId != null) {
                this.voiceStore.markEnded(this.pickedConversationId);
                this.pickedConversationId = null;
            }
            this.lastAttempt = null;
            this.context.set(null);
        });

        // KAN-238: the callee of OUR outbound call answered → the backend queues it as
        // WAITING_AGENT with direction "outbound". That frame is the trigger: pick it up
        // right away (a pickup before it answers 409). The queue widget / mobile ring
        // never list outbound rows, so nothing else reacts to it. A GET /voice/queue
        // re-sync hydrates the same row, so a missed frame is covered too.
        effect(() => {
            // Both are signals: the effect re-runs when the call is placed AND when a
            // frame lands, so an answer that raced the originate response is caught too.
            const pending = this.pendingOutbound();
            if (!pending) return;
            const events = Object.values(this.voiceStore.events());
            const answered = events.find(
                e =>
                    e.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                    isOutboundVoiceEvent(e) &&
                    this.matchesPending(e, pending.call)
            );
            if (answered) {
                void this.autoPickup(answered);
                return;
            }
            // The callee declined / the provider dropped the call before anyone answered.
            const ended = events.find(
                e =>
                    e.state === VOICE_CONVERSATION_STATE.ENDED &&
                    this.matchesPending(e, pending.call, false)
            );
            if (ended) this.abandonOutbound('engage.conversations.newCall.toasts.noAnswer');
        });

        // Bridge connected but the pickup flow errored/raced → put the UI back on track:
        // restore the call bar, drop the queue row and land on the caller's conversation.
        effect(() => {
            if (!this.onCall() || this.context() != null) return;
            const attempt = this.lastAttempt;
            if (!attempt) return;
            this.context.set(attempt.context);
            this.pickedConversationId = attempt.event.conversation_id;
            this.voiceStore.markPickedUp(attempt.event, this.loggedUserS.user()?.user_id ?? null);
            this.focusConversation(attempt.event.conversation_id);
        });
    }

    /** Register the Telnyx WebRTC client up-front (on entering the inbox). */
    register() {
        this.webrtc.register();
    }

    /**
     * Whether "pick up" can be offered for this queue entry right now. WhatsApp calls
     * need nothing registered (the browser negotiates on the spot); every other
     * provider needs the Telnyx SDK logged in with a sip_address to bridge to.
     */
    isPickupReady(event: Pick<VoiceConversationEvent, 'provider'> | undefined): boolean {
        if (isWhatsappVoiceCall(event)) return true;
        return this.registered();
    }

    /**
     * Re-sync the waiting queue from the server (GET /voice/queue). Called on widget
     * mount (the STOMP feed can't cover what happened before the tab opened) and after
     * a pickup 409 (someone else won the race or the wait just expired).
     */
    refreshQueue() {
        this.voiceS.getQueue$().subscribe({
            next: rows => this.voiceStore.hydrateQueue(rows ?? []),
            error: () => undefined,
        });
    }

    /**
     * Take a waiting call. `pickupToken` is only set on the automatic pickup of an
     * outbound call (KAN-238) — it proves this browser originated it.
     */
    async pickup(event: VoiceConversationEvent, context: VoiceCallContext, pickupToken?: string) {
        if (this.pickingUpId() != null) return;

        // One call at a time, whatever the leg: the Telnyx wrapper holds a single
        // activeCall and auto-answers, and the WhatsApp peer is one per browser.
        if (this.onCall()) {
            this.toast.error('voice.call.errors.alreadyOnCall');
            return;
        }

        if (isWhatsappVoiceCall(event)) {
            await this.pickupWhatsapp(
                event,
                {
                    ...context,
                    provider: VOICE_CALL_PROVIDER.whatsapp,
                },
                pickupToken
            );
            return;
        }

        // Must already be registered: send the SAME sip_address the SDK registered with.
        // Re-minting a token here would hand /pickup a fresh gencred that the SDK isn't
        // registered as → Telnyx rejects the bridge (call never connects).
        if (!this.registered() || !this.webrtc.sipAddress) {
            this.register();
            this.toast.error('voice.call.errors.notRegistered');
            return;
        }

        this.pickingUpId.set(event.conversation_id);
        this.context.set(context);
        this.lastAttempt = { event, context };
        try {
            // Prime the mic now so the SDK can answer the bridged invite with audio.
            if (!(await this.webrtc.ensureMicPermission())) {
                this.context.set(null);
                this.toast.error('voice.call.errors.micDenied');
                return;
            }
            await firstValueFrom(
                this.voiceS.pickupCall$(event.call_control_id, {
                    client_address: this.webrtc.sipAddress,
                    ...(pickupToken ? { pickup_token: pickupToken } : {}),
                })
            );
            // The backend bridges the call; the SDK receives the invite and auto-answers.
            // Flip the row to PICKED_UP locally — the STOMP frame may lag or be missed,
            // and the widget must stop offering a call that is already bridged.
            this.onPickedUp(event);
            this.armBridgeWatchdog();
        } catch (err) {
            this.context.set(null);
            this.toast.error(this.resolveError(err));
            this.handlePickupConflict(event, err);
        } finally {
            this.pickingUpId.set(null);
        }
    }

    /**
     * WhatsApp Cloud leg: mic → RTCPeerConnection → SDP offer on /pickup → answer. A
     * 502 means the SDP negotiation failed but the backend put the call back in
     * WAITING_AGENT, so the row stays and the agent can simply retry.
     */
    private async pickupWhatsapp(
        event: VoiceConversationEvent,
        context: VoiceCallContext,
        pickupToken?: string
    ) {
        this.pickingUpId.set(event.conversation_id);
        this.context.set(context);
        try {
            await this.whatsapp.pickup(event.call_control_id, pickupToken);
            this.onPickedUp(event);
            // ICE has to connect for audio to flow; if it never does, drop the leg.
            this.armBridgeWatchdog('voice.call.errors.bridgeLost', 15_000, () =>
                this.whatsapp.abort()
            );
        } catch (err) {
            this.context.set(null);
            if (err instanceof VoiceMicDeniedError) {
                this.toast.error('voice.call.errors.micDenied');
                return;
            }
            this.toast.error(this.resolveWhatsappError(err));
            this.handlePickupConflict(event, err);
        } finally {
            this.pickingUpId.set(null);
        }
    }

    /** Shared post-pickup bookkeeping: row off the queue, conversation ours, inbox on it. */
    private onPickedUp(event: VoiceConversationEvent) {
        this.pickedConversationId = event.conversation_id;
        this.voiceStore.markPickedUp(event, this.loggedUserS.user()?.user_id ?? null);
        this.assignToSelf(event.conversation_id);
        this.focusConversation(event.conversation_id);
    }

    /**
     * 409 = the call is no longer waiting (another agent won it, expired, abandoned):
     * drop the stale row immediately — the follow-up re-sync may fail silently and
     * must not be the only thing clearing it.
     */
    private handlePickupConflict(event: VoiceConversationEvent, err: unknown) {
        if ((err as { status?: number })?.status === 409) {
            this.voiceStore.dropWaiting(event.conversation_id);
            this.refreshQueue();
        }
    }

    /* ── Outbound (KAN-238) ─────────────────────────────────────── */

    /**
     * A human agent calls `to` from one of the account's numbers. The backend picks the
     * provider from the number and dials; we keep the originate 200 (provider_call_id +
     * pickup_token) and show "Llamando…" until the callee answers — at which point the
     * WAITING_AGENT/outbound frame triggers {@link autoPickup}. Resolves once the call
     * has left (or failed to): `ok: false` + `reason` when it never rang the callee —
     * `no_call_permission` is the WhatsApp opt-in gate and the UI should offer to
     * request it.
     */
    async placeCall(
        number: VoiceOutboundOrigin,
        to: string,
        context: Omit<VoiceCallContext, 'conversationId'>
    ): Promise<VoiceOutboundResult> {
        if (this.placingCall() || this.pendingOutbound()) return { ok: false, reason: 'error' };
        if (this.onCall()) {
            this.toast.error('voice.call.errors.alreadyOnCall');
            return { ok: false, reason: 'error' };
        }
        // A number with a PSTN leg may be dialled through Telnyx/Twilio, whose pickup
        // bridges to the SDK's registered sip_address — it must be logged in BEFORE we
        // dial, or the callee would answer to a leg we can't take. WhatsApp-only
        // numbers negotiate in the browser and need nothing registered.
        const mayUseTelnyx = !!number.provider;
        if (mayUseTelnyx && (!this.registered() || !this.webrtc.sipAddress)) {
            this.register();
            this.toast.error('voice.call.errors.notRegistered');
            return { ok: false, reason: 'error' };
        }

        this.placingCall.set(true);
        try {
            // Prime the mic now: the auto-pickup must not stall on a permission prompt
            // while the callee is already on the line.
            if (!(await this.webrtc.ensureMicPermission())) {
                this.toast.error('voice.call.errors.micDenied');
                return { ok: false, reason: 'error' };
            }
            const call = await firstValueFrom(this.voiceS.originateCall$(number.id, to));

            // 200 with status "failed": the call never left — no queue event will follow.
            // The endpoint answers 200 even when the call never left: `status` +
            // `disposition` say what actually happened, never the HTTP code. The modal
            // decides what to offer per disposition; only the generic one toasts here.
            if (call.status === VOICE_CALL_STATUS.failed) {
                const reason =
                    (call.disposition as VoiceCallDisposition | null | undefined) ??
                    VOICE_CALL_DISPOSITION.originationFailed;
                if (reason === VOICE_CALL_DISPOSITION.originationFailed) {
                    this.toast.error('engage.conversations.newCall.toasts.originationFailed');
                }
                return { ok: false, reason, call };
            }

            const fullContext: VoiceCallContext = {
                ...context,
                conversationId: call.conversation_id ?? 0,
                origin: context.origin ?? number.label ?? number.e164,
                provider: call.provider ?? null,
                outbound: true,
            };
            this.pendingOutbound.set({
                call,
                token: call.pickup_token ?? '',
                context: fullContext,
            });
            this.context.set(fullContext);
            this.outboundRinging.set(true);
            // Ringing the callee takes a while — give up (and tell the backend) after a
            // real timeout so the bar can't sit on "Llamando…" forever.
            clearTimeout(this.ringWatchdog);
            this.ringWatchdog = setTimeout(
                () => this.abandonOutbound('engage.conversations.newCall.toasts.noAnswer', true),
                75_000
            );
            // Land on the thread while it rings, when the backend already created it.
            if (call.conversation_id) this.focusConversation(call.conversation_id);
            return { ok: true, call };
        } catch (err) {
            this.toast.error(this.resolveOutboundError(err));
            return { ok: false, reason: 'error' };
        } finally {
            this.placingCall.set(false);
        }
    }

    /**
     * Does a queue event refer to the outbound call we are waiting on? The call id is
     * authoritative (the frame's call_control_id IS the originate's provider_call_id).
     * Only when the originate came back without one do we fall back on the
     * conversation — and never for terminal frames: the store keeps the ENDED of the
     * previous call on a reused conversation, which would abandon a fresh attempt.
     */
    private matchesPending(
        event: VoiceConversationEvent,
        call: VoiceCall,
        allowConversationFallback = true
    ): boolean {
        if (call.provider_call_id) return event.call_control_id === call.provider_call_id;
        return (
            allowConversationFallback &&
            !!call.conversation_id &&
            event.conversation_id === call.conversation_id
        );
    }

    /**
     * The callee answered: pick up without asking — the agent already pressed "call",
     * and every second here is silence on the callee's line. Guarded so the effect can
     * fire more than once for the same frame.
     */
    private async autoPickup(event: VoiceConversationEvent) {
        const pending = this.pendingOutbound();
        if (!pending || this.pickingUpId() != null) return;
        this.pendingOutbound.set(null);
        clearTimeout(this.ringWatchdog);
        this.outboundRinging.set(false);
        const context: VoiceCallContext = {
            ...pending.context,
            conversationId: event.conversation_id || pending.context.conversationId,
            provider: event.provider ?? pending.context.provider ?? null,
        };
        // The leg is decided by the provider of the CALL; the frame is authoritative,
        // the originate response is the fallback (a hydrated row may omit it).
        const asWhatsapp =
            isWhatsappVoiceCall(event) || (!event.provider && isWhatsappVoiceCall(pending.call));
        await this.pickup(
            asWhatsapp ? { ...event, provider: VOICE_CALL_PROVIDER.whatsapp } : event,
            context,
            pending.token || undefined
        );
    }

    /**
     * Stop waiting for an outbound call: callee never answered, backend reported it
     * ended, or the agent hung up while it rang. `tellBackend` cancels the ringing leg
     * (best effort — a call that already ended just 4xx's).
     */
    private abandonOutbound(messageKey?: string, tellBackend = false) {
        const pending = this.pendingOutbound();
        if (!pending) return;
        this.pendingOutbound.set(null);
        clearTimeout(this.ringWatchdog);
        this.outboundRinging.set(false);
        this.context.set(null);
        if (tellBackend && pending.call.provider_call_id) {
            this.voiceS
                .hangupCall$(pending.call.provider_call_id)
                .subscribe({ error: () => undefined });
        }
        if (messageKey) this.toast.info(messageKey);
    }

    toggleMute() {
        if (this.activeLeg() === 'whatsapp') this.whatsapp.toggleMute();
        else this.webrtc.toggleMute();
    }

    hangup() {
        clearTimeout(this.bridgeWatchdog);
        // Outbound still ringing: nothing is bridged yet — cancel the attempt instead.
        if (this.pendingOutbound() && !this.onCall()) {
            this.abandonOutbound(undefined, true);
            return;
        }
        if (this.activeLeg() === 'whatsapp') {
            void this.whatsapp.hangup();
        } else {
            this.webrtc.hangup();
        }
        // Clear immediately; callEnded$ also fires, but the panel shouldn't linger.
        // Also drop the attempt or the restore-effect would resurrect the bar while
        // the SDK still reports 'active' for the leg being torn down.
        this.lastAttempt = null;
        this.context.set(null);
    }

    /**
     * Land the agent on the caller's conversation right after picking up, so they see
     * the thread of the person they're talking to. Uses the same `?convo=` deep-link
     * as browser notifications — inb-convos selects it once the list contains it.
     */
    private focusConversation(conversationId: number) {
        // On the phone the inbox is the mobile shell — the chat view is its own route.
        if (this.detectorS.isMobile()) {
            void this.router.navigate(
                ['/', APP_SLUGS.mobile, MOBILE_SLUGS.inbox, String(conversationId)],
                { replaceUrl: this.router.url.includes(`/${MOBILE_SLUGS.inbox}/`) }
            );
            return;
        }
        void this.router.navigate([`/${APP_SLUGS.MAIN}`, MAIN_SLUGS.inbox, 'conversations'], {
            queryParams: { convo: conversationId },
        });
    }

    /**
     * A 200 on /pickup doesn't guarantee the call connects — the caller may hang up in
     * that same instant (or ICE may never complete on the WhatsApp leg), leaving the
     * call panel (and any directed ring) stuck until a browser refresh. If the call
     * isn't active shortly after the pickup, drop the panel, run the leg-specific
     * cleanup and re-sync the queue from the server.
     */
    private armBridgeWatchdog(
        messageKey = 'voice.call.errors.bridgeLost',
        timeoutMs = 15_000,
        onLost?: () => void
    ) {
        clearTimeout(this.bridgeWatchdog);
        this.bridgeWatchdog = setTimeout(() => {
            if (this.onCall() || !this.context()) return;
            onLost?.();
            this.context.set(null);
            this.pickedConversationId = null;
            this.toast.error(messageKey);
            this.refreshQueue();
        }, timeoutMs);
    }

    /** Assign the conversation to the agent who took the call (keeping its department). */
    private assignToSelf(conversationId: number) {
        const userId = this.loggedUserS.user()?.user_id;
        if (!userId) return;
        const params = {
            convoId: conversationId,
            userId,
            departmentId: this.context()?.departmentId,
        } as ConversationAssignmentUpdateParams;
        this.assignmentsHttpS.updateAssignee$(params).subscribe({ error: () => undefined });
    }

    private resolveOutboundError(err: unknown): string {
        switch ((err as { status?: number })?.status) {
            case 503:
                return 'voice.call.errors.webrtcUnavailable';
            case 400:
                return 'engage.conversations.newCall.toasts.emptyDestination';
            case 409:
                // The origin number isn't ACTIVE.
                return 'engage.conversations.newCall.toasts.numberInactive';
            case 404:
            case 405:
                // Number not ours / the endpoint isn't deployed on this backend yet.
                return 'engage.conversations.newCall.toasts.notSupported';
            default:
                return 'engage.conversations.newCall.toasts.error';
        }
    }

    private resolveError(err: unknown): string {
        switch ((err as { status?: number })?.status) {
            case 503:
                return 'voice.call.errors.webrtcUnavailable';
            case 409:
                return 'voice.call.errors.alreadyTaken';
            case 403:
                // Outbound of another user, or the pickup_token is missing/wrong (KAN-238).
                return 'voice.call.errors.pickupForbidden';
            case 400:
                return 'voice.call.errors.missingClient';
            default:
                return 'voice.call.errors.pickupFailed';
        }
    }

    private resolveWhatsappError(err: unknown): string {
        switch ((err as { status?: number })?.status) {
            case 409:
                return 'voice.call.errors.alreadyTaken';
            case 403:
                return 'voice.call.errors.pickupForbidden';
            case 502:
                return 'voice.call.errors.whatsappAudioFailed';
            default:
                return 'voice.call.errors.pickupFailed';
        }
    }
}
